import React, { useState, useEffect } from "react";
import axios from "axios";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Button, Select, MenuItem } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import * as XLSX from "xlsx";

function TransactionList() {
  const getStartOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1); // First day of the current month
  };

  const getToday = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Today's date
  };

  const [filters, setFilters] = useState({
    fromDate: getStartOfMonth(),
    toDate: getToday(),
    status: "",
    mode: "",
    page: 1,
    pageSize: 5,
  });

  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch Data from API
  const fetchData = async () => {
    const { fromDate, toDate, status, mode, page, pageSize } = filters;
    setLoading(true);

    const requestData = {
      from_transaction_date: fromDate
        ? new Date(fromDate.setHours(0, 0, 0, 0))
            .toISOString()
            .slice(0, 19)
        : null,
      to_transaction_date: toDate
        ? new Date(toDate.setHours(23, 59, 59, 999))
            .toISOString()
            .slice(0, 19)
        : null,
      status: status || undefined,
      mode: mode || undefined,
      page: page,
      page_size: pageSize,
    };

    try {
      const response = await axios.post("https://api.appmingleconsultants.com/paymingle/listTransactions/", requestData);
      setFilteredData(response.data.results);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component load
  useEffect(() => {
    fetchData(); // Trigger API call with default filters on load
  }, []);

  // Apply Filters
  const applyFilters = () => {
    fetchData();
  };

  // Clear Filters
  const clearFilters = () => {
    setFilters({ fromDate: getStartOfMonth(), toDate: getToday(), status: "", mode: "", page: 1, pageSize: 5 });
    setFilteredData([]);
  };

  // Export Data to Excel
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Filtered Data");
    XLSX.writeFile(workbook, "filtered_data.xlsx");
  };

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Paper style={{ padding: "20px", margin: "20px" }}>
          <h2>Transaction Data Viewer</h2>

          {/* FilterBar */}
          <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
            <DatePicker
              label="From Date"
              value={filters.fromDate}
              onChange={(date) => setFilters({ ...filters, fromDate: date })}
              renderInput={(params) => <TextField {...params} />}
            />

            <DatePicker
              label="To Date"
              value={filters.toDate}
              onChange={(date) => setFilters({ ...filters, toDate: date })}
              renderInput={(params) => <TextField {...params} />}
            />

            <Select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              displayEmpty
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Failed">Failed</MenuItem>
            </Select>

            <Select
              value={filters.mode}
              onChange={(e) => setFilters({ ...filters, mode: e.target.value })}
              displayEmpty
            >
              <MenuItem value="">All Modes</MenuItem>
              <MenuItem value="Online">Online</MenuItem>
              <MenuItem value="Offline">Offline</MenuItem>
            </Select>

            <Button variant="contained" onClick={applyFilters} disabled={loading}>
              Apply Filters
            </Button>
            <Button variant="outlined" onClick={clearFilters} disabled={loading}>
              Clear Filters
            </Button>
          </div>

          {/* Data Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User Name</TableCell>
                  <TableCell>Mobile Number</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Redemption Date</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Transaction Date</TableCell>
                  <TableCell>UTR</TableCell>
                  <TableCell>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData?.length > 0 ? (
                  filteredData?.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.uname}</TableCell>
                      <TableCell>{row.mobnumber}</TableCell>
                      <TableCell>{row.amount}</TableCell>
                      <TableCell>{new Date(row.redemptiondate).toLocaleString()}</TableCell>
                      <TableCell>{row.mode}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>{new Date(row.trsacationdate).toLocaleString()}</TableCell>
                      <TableCell>{row.utr}</TableCell>
                      <TableCell>{row.remarks}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} style={{ textAlign: "center" }}>
                      {loading ? "Loading..." : "No data found."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Download Button */}
          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: "20px" }}
            onClick={exportToExcel}
            disabled={loading || filteredData?.length === 0}
          >
            Download Filtered Data
          </Button>
        </Paper>
      </LocalizationProvider>
    </>
  );
}

export default TransactionList;
